import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LangSwitcher from './ui/LangSwitcher';
import SocialLink from './ui/SocialLink';
import { DEFAULT_SOCIAL_URLS } from '../utils/socialLinks';

const Header = () => {
  const [, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isLogoModalOpen, setIsLogoModalOpen] = useState(false);
  const location = useLocation();
  const { t } = useTranslation();

  const menuItems = [
    { href: "/", text: t('nav.home'), icon: "fas fa-home" },
    { href: "/shree-radhe-krishna-bapu", text: t('nav.about'), icon: "fas fa-user-circle" },
    { href: "/events", text: t('nav.events'), icon: "fas fa-calendar-alt" },
    { href: "/katha", text: t('nav.katha'), icon: "fas fa-play-circle" },
    { href: "/gallery", text: t('nav.gallery'), icon: "fas fa-images" },
    { href: "/contact", text: t('nav.contact'), icon: "fas fa-map-marker-alt" }
  ];

  useEffect(() => {
    const handleScroll = () => {
      const header = document.getElementById('header');
      if (window.scrollY > 50) {
        setIsScrolled(true);
        if (header) header.classList.add('scrolled');
      } else {
        setIsScrolled(false);
        if (header) header.classList.remove('scrolled');
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  return (
    <header
      id="header"
      className="bg-white shadow-lg fixed w-full top-0 left-0 right-0 z-50 transition-all duration-300 h-20"
    >
      {/* Language switcher — fixed to far right of header, desktop only */}
      <div className="hidden lg:flex absolute top-0 h-20 items-center z-10" style={{ right: '50px' }}>
        <LangSwitcher />
      </div>

      <div className="container mx-auto px-4 relative h-full">
        <div className="flex items-center h-full">
          {/* Logo Section */}
          <div className="flex items-center space-x-3 select-none flex-shrink-0">
            <button
              onClick={() => setIsLogoModalOpen(true)}
              className="focus:outline-none focus:ring-0 cursor-pointer group"
            >
              <img
                src="/logo.jpg"
                alt={t('header.logo_alt')}
                className="h-16 w-16 rounded-full object-cover shadow-lg border-2 border-orange-400 group-hover:scale-105 transition-transform duration-300"
              />
            </button>
            <Link to="/" className="block focus:outline-none focus:ring-0">
              <h1 className="text-xl md:text-2xl font-bold text-orange-500 leading-tight hover:text-orange-600 transition-colors">
                {t('header.site_name')}
              </h1>
              <p className="text-xs md:text-sm text-gray-600 leading-tight font-medium">
                {t('header.site_tagline')}
              </p>
            </Link>
          </div>

          {/* Desktop Navigation — sits left of center, near logo */}
          <nav className="hidden lg:block ml-6 xl:ml-8">
            <ul className="flex items-center space-x-5">
              {menuItems.map((item) => (
                <li key={item.href}>
                  <Link
                    to={item.href}
                    className={`nav-link font-medium transition-colors duration-200 px-3 py-2 rounded-md text-base ${location.pathname === item.href
                      ? 'text-orange-600 bg-orange-50'
                      : 'text-gray-700 hover:text-orange-600 hover:bg-orange-50'
                      }`}
                    style={{ fontSize: '1rem' }}
                  >
                    {item.text}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>

          {/* Action Buttons */}
          <div className="hidden lg:flex items-center space-x-3 ml-auto">
            <Link
              to="/donate"
              className="btn-sacred text-sm px-4 py-2 bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-full hover:from-orange-600 hover:to-red-600 transition-all duration-200 shadow-md hover:shadow-lg flex items-center"
              style={{ fontSize: '0.875rem' }}
            >
              <i className="fas fa-heart mr-2"></i>
              {t('nav.seva_donation')}
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={toggleMobileMenu}
            className="lg:hidden ml-auto text-gray-700 hover:text-orange-600 p-3 rounded-md hover:bg-orange-50 transition-all duration-200 border border-gray-300 hover:border-orange-300 bg-white shadow-sm"
            style={{ minWidth: '44px', minHeight: '44px' }}
          >
            <i className={`fas ${isMobileMenuOpen ? 'fa-times' : 'fa-bars'} text-xl`}></i>
          </button>
        </div>

      </div>

        {/* Mobile Navigation — full-screen overlay with dim backdrop */}
        {isMobileMenuOpen && createPortal(
          <div
            className="fixed inset-0 lg:hidden"
            style={{ zIndex: 49 }}
          >
            {/* Dim backdrop — tap outside to close */}
            <button
              aria-label={t('header.close_menu')}
              onClick={closeMobileMenu}
              className="absolute inset-0 w-full h-full bg-black bg-opacity-40 border-0 cursor-default"
              style={{ WebkitTapHighlightColor: 'transparent' }}
            />

            {/* Menu panel */}
            <div
              className="absolute left-0 right-0 bg-white shadow-2xl"
              style={{ top: '80px', zIndex: 1 }}
              onClick={(e) => e.stopPropagation()}
            >
              <nav className="flex flex-col space-y-1 p-4">
                {menuItems.map((item) => (
                  <Link
                    key={item.href}
                    to={item.href}
                    onClick={closeMobileMenu}
                    className={`flex items-center px-4 py-3 rounded-lg font-medium transition-all duration-200 ${location.pathname === item.href
                      ? 'text-orange-600 bg-orange-50'
                      : 'text-gray-700 hover:bg-orange-50 hover:text-orange-600'
                      }`}
                  >
                    <i className={`${item.icon} w-5 text-orange-500 mr-3`}></i>
                    <span>{item.text}</span>
                  </Link>
                ))}

                {/* Mobile Action Buttons */}
                <div className="px-4 py-4 border-t border-orange-100 space-y-3">
                  <Link
                    to="/donate"
                    onClick={closeMobileMenu}
                    className="btn-sacred text-sm px-4 py-4 bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-full hover:from-orange-600 hover:to-red-600 transition-all duration-200 shadow-md hover:shadow-lg w-full flex items-center justify-center"
                    style={{ minHeight: '52px' }}
                  >
                    <i className="fas fa-heart mr-2"></i>
                    <span>{t('nav.seva_donation')}</span>
                  </Link>
                  {/* Language Switcher — below Donation button */}
                  <div className="flex justify-center py-1" style={{ marginTop: '28px' }}>
                    <LangSwitcher />
                  </div>
                </div>
              </nav>
            </div>
          </div>,
          document.body
        )}

      {/* Logo High-Res Preview Modal rendered via Portal */}
      {isLogoModalOpen && createPortal(
        <div
          style={{ zIndex: 99999, backgroundColor: 'rgba(0, 0, 0, 0.85)' }}
          className="fixed inset-0 backdrop-blur-md flex items-center justify-center p-4 cursor-pointer"
          onClick={() => setIsLogoModalOpen(false)}
        >
          <div
            style={{ zIndex: 100000 }}
            className="relative bg-white rounded-3xl p-6 md:p-8 max-w-lg w-full shadow-2xl border-4 border-orange-300 text-center my-auto cursor-default"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button */}
            <button
              onClick={() => setIsLogoModalOpen(false)}
              className="absolute top-4 right-4 bg-gray-100 hover:bg-orange-100 text-gray-700 hover:text-orange-600 w-10 h-10 rounded-full flex items-center justify-center text-lg font-bold transition-colors focus:outline-none"
              title="Close Preview"
            >
              <i className="fas fa-times"></i>
            </button>

            <div className="inline-flex items-center bg-orange-100 rounded-full px-4 py-1.5 mb-3">
              <i className="fas fa-om text-orange-600 mr-2"></i>
            </div>

            <h3 className="text-xl md:text-2xl font-bold text-gray-800 mb-1">{t('header.modal.title')}</h3>
            <p className="text-xs text-orange-600 mb-4 font-semibold">{t('header.modal.subtitle')}</p>

            <div className="relative overflow-hidden rounded-2xl border-2 border-orange-200 shadow-xl bg-orange-50/50 p-2 mb-6">
              <img
                src="/logo.jpg"
                alt={t('header.modal.logo_alt')}
                style={{ maxHeight: '55vh' }}
                className="w-full h-auto object-contain rounded-xl mx-auto shadow-md"
              />
            </div>

            <SocialLink
              href={DEFAULT_SOCIAL_URLS.youtube}
              className="inline-flex items-center justify-center px-8 py-3.5 bg-red-600 hover:bg-red-700 text-white font-bold text-base rounded-full transition-all duration-300 shadow-xl hover:scale-105"
            >
              <i className="fab fa-youtube mr-3 text-xl animate-pulse"></i>
              {t('header.modal.subscribe')}
              <i className="fas fa-bell ml-2 text-sm text-white/80 animate-bounce"></i>
            </SocialLink>
          </div>
        </div>,
        document.body
      )}
    </header>
  );
};

export default Header;
